<template>
  <div>
    <!-- Header -->
    <m-row>
      <m-col lg="12">
        <h3 class="title">{{ $t('MEMBER_RENEW_CAMPAIGN.SCHEDULED_SENDOUTS.TITLE') }}</h3>
        <p v-if="!disabled" class="subtitle">{{ $t('MEMBER_RENEW_CAMPAIGN.SCHEDULED_SENDOUTS.SUBTITLE') }}</p>
      </m-col>
    </m-row>

    <!-- Sendouts list -->
    <m-row class="mt-4">
      <m-col lg="12">
        <div class="sendouts-list">
          <RenewCampaignScheduledSendoutRow
            v-for="(row, idx) in local_sendouts"
            :key="row._key"
            :index="idx"
            :row_id="row.id"
            :renew_settings="renew_settings"
            :campaign_id="campaign_id"
            @created="row_created"
            @updated="row_updated"
            @deleted="row_deleted"
            :disabled="disabled"
          />
          
          <div v-if="local_sendouts.length === 0" class="empty">
            {{ $t('MEMBER_RENEW_CAMPAIGN.SCHEDULED_SENDOUTS.EMPTY') }}
          </div>
        </div>
      </m-col>
    </m-row>

    <!-- Add new row section -->
    <m-row class="mt-5 add-sendout-row" v-if="!disabled">
      <m-col lg="8" md="12">
        <memlist-icon-button @click="add_sendout_row" :text="$t('MEMBER_RENEW_CAMPAIGN.SCHEDULED_SENDOUTS.ADD_NEW')" svg="/assets/svg/plus-square.svg" variant="success">{{ $t('MEMBER_RENEW_CAMPAIGN.SCHEDULED_SENDOUTS.ADD_NEW') }}</memlist-icon-button>
        
      </m-col>
    </m-row>

    
  </div>
</template>

<script>
import axios from 'axios'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapGetters } from 'vuex'
import { toasts } from '@/core/mixins/toastr-helper.mixin.js'
import RenewCampaignScheduledSendoutRow from './RenewCampaignScheduledSendoutRow.vue';
import dayjs from 'dayjs';

/**
 * RenewCampaignScheduledSendoutEditorModal
 *
 * Props:
 *  - campaign_id: Number | String (required to load/save)
 *  - sendout_types: Array<{ text: string, value: string }>
 *      Optional. Defaults to EMAIL/SMS/LETTER.
 *
 * Emits:
 *  - updated(sendouts)
 *  - closed()
 */
export default {
  name: 'RenewCampaignScheduledSendouts',
  mixins: [toasts],
  components: {
    RenewCampaignScheduledSendoutRow,
  },
  props: {
    disabled: { type: Boolean, default: false },
    campaign_id: { type: [Number, String], required: true },
  },
  setup () {
    return { v$: useVuelidate() }
  },
  data () {
    return {
      renew_settings: {},
      campaign: null,
      local_sendouts: [],
      deleted_ids: [],
      templates: [],
    }
  },
  computed: {
    sendout_types() {
      return [
        { text: this.$t('MEMBER_RENEW.SENDOUT_METHODS.EMAIL'), value: 'EMAIL' },
        { text: this.$t('MEMBER_RENEW.SENDOUT_METHODS.SMS'), value: 'SMS' },
        { text: this.$t('MEMBER_RENEW.SENDOUT_METHODS.POST'), value: 'POST' },
        { text: this.$t('MEMBER_RENEW.SENDOUT_METHODS.INVOICE'), value: 'INVOICE' },
        { text: this.$t('MEMBER_RENEW.SENDOUT_METHODS.CALL'), value: 'CALL' },
      ];
    },
    email_template_options() {
      return this.templates.filter(i => i.type === 'EMAIL').map(i => ({ text: i.name, value: i.template_id }));
    },
    sms_template_options() {
      return this.templates.filter(i => i.type === 'SMS').map(i => ({ text: i.name, value: i.template_id }));
    },
    ...mapGetters(['currentCompanyId', 'currentPeriodId', 'settings']),
    rules () {
      // Build a dynamic Vuelidate schema for each row
      const rowRule = { type: { required }, scheduled_at: { required } }
      const rowsRules = {}
      this.local_sendouts.forEach((row, i) => { rowsRules[i] = rowRule })
      return { local_sendouts: rowsRules }
    }
  },
  validations () { return this.rules },
  watch: {
    campaign_id: {
      immediate: true,
      handler (val) {
        if (val) {
          this.fetch_campaign_meta(val)
          this.fetch_sendouts(val)
        }
      }
    }
  },
  methods: {
    row_created(item) {
      this.local_sendouts.push(item);
    },
    row_updated(item) {},
    row_deleted(id) {
      this.local_sendouts = this.local_sendouts.filter(i => i.id !== id);
    },

    async fetch_settings() {

      const res = await axios.get(`/company/admin?company_id=${this.currentCompanyId}`);
      
      if (res.status === 200) {
        if (res.data.renew) {
          this.renew_settings = res.data.renew;
        }
      }
    },

    async fetch_campaign_meta (id) {
      try {
        const res = await axios.get(`/member_renew/campaign/${id}`)
        if (res.status === 200) this.campaign = res.data
      } catch (e) { /* non-fatal */ }
    },

    async fetch_sendouts (id) {
      try {
        const res = await axios.get(`/member_renew/campaign/${id}/schedule/sendouts`)
        if (res.status === 200 && Array.isArray(res.data)) {
          // Normalize and add local keys
          this.local_sendouts = res.data;
        } else {
          this.local_sendouts = []
        }
      } catch (e) {
        this.local_sendouts = []
      }
    },

    // Add/Delete rows (client-side)
    async add_sendout_row () {

      let max_scheduled_at = dayjs().format('YYYY-MM-DD');

      for (const s of this.local_sendouts) {
        if (s.scheduled_at > max_scheduled_at) {
          max_scheduled_at = s.scheduled_at;
        }
      }

      const res = await axios.post(`/member_renew/campaign/${this.campaign_id}/schedule/sendout`, { 
        sendout_method: 'EMAIL',
        scheduled_at: dayjs().add(3, 'days').format('YYYY-MM-DD')
      });

      if (res.status === 201) {
        console.log('ok');

        this.local_sendouts.push(res.data);

        // Ensure validation touches the new row on first save
        this.$nextTick(() => { this.v$ && this.v$.$touch() });
      }
      else {
        console.error('unable to create sendout', res.status);
      }
      
    },
  }
}
</script>

<style scoped>
.title { margin: 0; font-size: 20px; font-weight: 600; }
.subtitle { margin-top: 4px; color: #666; }
.sendouts-list { display: grid; grid-template-columns: 1fr; gap: 16px; }
.sendout-box { border: 1px solid #e3e6ef; border-radius: 12px; padding: 12px; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.03); }
.sendout-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.sendout-title { display: flex; align-items: center; gap: 8px; font-weight: 600; }
.id-pill { font-size: 12px; background: #f3f4f6; color: #374151; padding: 2px 8px; border-radius: 999px; }
.sendout-actions { display: flex; gap: 8px; }
.empty { padding: 24px; text-align: center; color: #6b7280; border: 1px dashed #d1d5db; border-radius: 12px; }
.add-row { display: flex; justify-content: flex-start; }

.add-sendout-row {
  border-top: 1px solid #e5e7eb;
  padding-top: 16px;
}
.add-sendout-label {
  font-weight: 500;
  font-size: 14px;
}
.add-sendout-btn {
  display: flex;
  justify-content: flex-end;
}


</style>
